import ConfirmDialog from "../../components/customHelpers/ConfirmDialog";
import Controls from "../../components/controls/Controls";
import Norecords from "../../components/customHelpers/Norecords";
import Loading from "../../components/customHelpers/Loading";
import Layout from "../../components/layout/Layout";
import {
  Grid,
  Box,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Paper,
  Select,
  MenuItem,
  Toolbar,
  Button,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import useTable from "../../components/customHelpers/useTable";
import Notify from "../../components/customHelpers/Notify";
import Notification from "../../components/customHelpers/Notification";
import Admin from "../../components/auth/Admin";
import Order from "../../actions/order";
import { FiEye } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatNumber } from "../../utils/utils";
import CustomerDetails from "../admin/order/orderProfiel";
import { statusBadge } from "../../components/statusBadge";
import InfoComponent from "../../components/InfoComponent";

const headCells = [
  { id: "id", label: "Id" },
  { id: "assignTo", label: "Assigned To" },
  { id: "totalPrice", label: "Total Price" },
  { id: "paid", label: "Paid" },
  { id: "unpaid", label: "Balance" },
  { id: "createdAt", label: "Date" },
  { id: "status", label: "Status" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const useStyles = makeStyles(() => ({
  notFoundContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "50vh",
    textAlign: "center",
    padding: "24px",
  },
  notFoundTitle: {
    color: "#d32f2f", 
    marginBottom: "16px",
    fontWeight: "bold",
  },
  notFoundMessage: {
    color:  "rgba(244,151,3,0.8)",
    marginBottom: "24px", 
    maxWidth: "500px",
  },
}));

const CustomerOrderPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { NotifyMessage, notify, setNotify } = Notify();
  const { viewCustomerOrders } = Order();
  const [customer, setCustomer] = useState(null);
  const [customerNotFound, setCustomerNotFound] = useState(false); // New state for customer not found
  const value = location.pathname.split("/").filter((x) => x)[
    location.pathname.split("/").filter((x) => x).length - 1
  ];

  const [totals, setTotals] = useState({
    totalPaid: 0,
    totalUnpaid: 0,
  });

  const [orders, setOrders] = useState([]);
  const [values, setValues] = useState({
    loading: false,
    reload: false,
  });
  const { loading, reload } = values;

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const [additionalConfirmDialog, setAdditionalConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const [filterFn, setFilterFn] = useState({
    fn: (items) => items,
  });

  const getAllOrders = () => {
    setValues({ ...values, loading: true });
    viewCustomerOrders(value).then((data) => {
      setValues({ ...values, loading: false });
      if (data.err || !data?.customer) {
        // Handle error or no customer found
        setCustomerNotFound(true);
        NotifyMessage({
          message: data.err || "Customer not found",
          type: "error",
        });
      } else {
        setCustomerNotFound(false);
        setCustomer(data);
        const filteredOrders = data.orders.filter(
          (order) => order.status !== "Pending"
        );

        setOrders(filteredOrders);
        let totalPaid = 0;
        let totalUnpaid = 0;

        filteredOrders.forEach((order) => {
          const orderPaid = parseFloat(order.paid) || 0;
          const totalOrderPrice =
            order.orderProducts?.reduce(
              (sum, product) => sum + (parseFloat(product.totalPrice) || 0),
              0
            ) || 0;

          totalPaid += orderPaid;
          totalUnpaid += totalOrderPrice - orderPaid;
        });

        setTotals({
          totalPaid: totalPaid.toFixed(2),
          totalUnpaid: totalUnpaid.toFixed(2),
        });
      }
    });
  };

  useEffect(() => {
    getAllOrders();
  }, [reload]);

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable(orders, headCells, filterFn);

  const handleSearch = (e) => {
    e.target.value = e.target.value.trimLeft();
    let target = e.target;
    setFilterFn({
      fn: (items) => {
        const columns = ["status", "customer", "id"];
        if (target.value === "") return items;
        else {
          return items.filter((x) =>
            columns.some(
              (column) =>
                x[column]
                  ?.toString()
                  .toLowerCase()
                  .indexOf(target.value.toLowerCase()) > -1
            )
          );
        }
      },
    });
  };

  const calculateTotalPrice = (data) => {
    let totalPrice = 0;
    if (data?.length > 0) {
      data.forEach((order) => {
        totalPrice += parseFloat(order.totalPrice) || 0;
      });
      return totalPrice.toFixed(2);
    }
    return "0.00";
  };

  // Render "Customer Not Found" message
  if (customerNotFound) {
    return (
      <Layout>
        <Admin>
          <Typography variant="h6" className="page-header">
            <ArrowBackIcon
              style={{ cursor: "pointer" }}
              onClick={() => history.push("/customers")}
            />
            <span style={{ marginLeft: "6px" }}>Customer Orders</span>
          </Typography>
          <Box className={classes.notFoundContainer}>
            <Typography variant="h4" className={classes.notFoundTitle}>
              Customer Not Found
            </Typography>
            <Typography variant="body1" className={classes.notFoundMessage}>
              The customer with ID {value} could not be found. Please check the
              customer ID and try again.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/customers")}
            >
              Back to Customers
            </Button>
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
        </Admin>
      </Layout>
    );
  }

  return (
    <Layout>
      <Admin>
        <div className="card">
          <Typography
            variant="h6"
            style={{ marginBottom: "0px" }}
            className="page-header"
          >
            <ArrowBackIcon
              style={{ cursor: "pointer" }}
              onClick={() => history.push("/customers")}
            />
            <span style={{ marginLeft: "6px" }}>Customer Orders</span>
          </Typography>
          <InfoComponent pageKey="orders" />
          {customer?.customer && (
            <CustomerDetails title="Customer" id={value} product={customer} />
          )}
          <Toolbar disableGutters>
            <div
              className="search-header-container"
              style={{
                marginBottom: 0,
                marginLeft: 0,
                flex: 1,
                maxWidth: 260,
                minWidth: 260,
              }}
            >
              <div className="search-header-flex">
                <div className="search-container">
                  <Search className="search_icon" />
                  <input
                    type="text"
                    onChange={handleSearch}
                    placeholder="Search Orders"
                    className="search-input"
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            </div>
          </Toolbar>
          <Grid container>
            <Grid item xs={12} className="card__body">
              {loading ? (
                <Loading />
              ) : (
                <Paper style={{ overflowX: "auto" }}>
                  <TblContainer>
                    <TblHead />
                    <TableBody>
                      {recordsAfterPagingAndSorting().length > 0 ? (
                        recordsAfterPagingAndSorting().map((item) => (
                          <TableRow key={item.id} style={{ width: "10%" }}>
                            <TableCell>
                              {item.orderId
                                ? item.orderId
                                : `${item.id.slice(0, 7)}--`}
                            </TableCell>
                            <TableCell
                              className="page-table-cell"
                              style={{ width: "10%" }}
                            >
                              <div className="page-assigned-to">
                                {item.staff?.fullName}
                              </div>
                            </TableCell>
                            <TableCell className="page-table-cell">
                              <div className="page-total">
                                {item?.orderProducts?.length > 0
                                  ? formatNumber(
                                      calculateTotalPrice(item.orderProducts)
                                    )
                                  : 0}
                              </div>
                            </TableCell>
                            <TableCell
                              className="page-table-cell hidden sm:table-cell"
                              style={{ width: "10%" }}
                            >
                              <div className="page-paid">
                                {formatNumber(item.paid)}
                              </div>
                            </TableCell>
                            <TableCell
                              className="page-table-cell hidden sm:table-cell"
                              style={{
                                minwidth: "200px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <div
                                className={`page-balance ${
                                  parseFloat(
                                    (
                                      Number(
                                        calculateTotalPrice(
                                          item.orderProducts
                                        ) || 0
                                      ) - Number(item.paid || 0)
                                    ).toString()
                                  ) < 0
                                    ? "text-red-600"
                                    : parseFloat(
                                        (
                                          Number(
                                            calculateTotalPrice(
                                              item.orderProducts
                                            ) || 0
                                          ) - Number(item.paid || 0)
                                        ).toString()
                                      ) > 0
                                    ? "text-amber-600"
                                    : "text-emerald-600"
                                }`}
                              >
                                {formatNumber(
                                  Number(
                                    calculateTotalPrice(item.orderProducts) || 0
                                  ) - Number(item.paid || 0)
                                )}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`page-table-cell `}
                              style={{
                                minWidth: "250px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {new Date(item.createdAt).toLocaleString()}
                            </TableCell>
                            <TableCell className="page-table-cell">
                              {statusBadge(item.status || "Unknown")}
                            </TableCell>
                            <TableCell>
                              <Controls.ActionButton
                                color="primary"
                                title="View Detail"
                                onClick={() => {
                                  history.push(`/print/${item.id}`);
                                }}
                              >
                                <FiEye fontSize="medium" />
                              </Controls.ActionButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <Norecords col={6} />
                      )}
                    </TableBody>
                  </TblContainer>
                  <TblPagination />
                </Paper>
              )}
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "column",
              p: 1,
              m: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <Box p={1}>
              <span style={{ marginRight: "10px" }}>Total Paid</span>
              <TextField
                value={formatNumber(totals.totalPaid)}
                variant="outlined"
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    padding: "6px 8px",
                  },
                }}
              />
            </Box>
            <Box p={1}>
              <span style={{ marginRight: "10px" }}>Total Balance</span>
              <TextField
                value={formatNumber(totals.totalUnpaid)}
                variant="outlined"
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    padding: "6px 8px",
                  },
                }}
              />
            </Box>
          </Box>
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <ConfirmDialog
            confirmDialog={additionalConfirmDialog}
            setConfirmDialog={setAdditionalConfirmDialog}
          />
          <Notification notify={notify} setNotify={setNotify} />
        </div>
      </Admin>
    </Layout>
  );
};

export default CustomerOrderPage;
